import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, ChevronRight, X, Sparkles, Loader2, Save, Eye, Globe, Lock, Camera, Video, Mail as MailIcon, Send, MessageCircle, ChevronDown, ChevronUp, Check, ExternalLink } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import UnlockBar from "../components/UnlockBar";
import PublishNudge from "../components/PublishNudge";

/* ──────────────────────────── HELPERS ──────────────────────────── */

const scoreLabel = (s) => s >= 80 ? "Strong" : s >= 60 ? "Good" : s >= 40 ? "Building" : "Just started";
const scoreColor = (s) => s >= 80 ? "#7FD17F" : s >= 60 ? "#D4AF37" : s >= 40 ? "#D4AF37" : "#888";

const POSITIONS = [
  "Goalkeeper", "Right-Back", "Left-Back", "Centre-Back", "Right Centre-Back", "Left Centre-Back",
  "Defensive Midfielder", "Centre Midfielder", "Attacking Midfielder", "Box-to-Box Midfielder",
  "Right Midfielder", "Left Midfielder", "Right Winger", "Left Winger",
  "Centre-Forward", "Striker", "Second Striker", "False 9"
];
const FEET = ["Right", "Left", "Both"];
const AGENT_STATUSES = ["No agent", "Searching", "Has agent"];

const FIELD_LABELS = {
  headline: "Headline",
  overview: "Overview",
  playing_style: "Playing Style",
  tactical_in_possession: "Tactical · In Possession",
  tactical_out_of_possession: "Tactical · Out of Possession",
  tactical_transitions: "Tactical · Transitions",
  development_focus: "Development Focus",
  experience_summary: "Experience",
  highlight_video: "Highlight Video",
  contact_email: "Contact Email",
};

/* ──────────────────────────── COMPONENTS ──────────────────────────── */

function SectionRow({ label, value, onTap, testId, badge }) {
  const snippet = (value || "").trim();
  const preview = snippet ? (snippet.length > 110 ? snippet.slice(0, 110) + "…" : snippet) : null;
  return (
    <button
      onClick={onTap}
      className="w-full text-left bg-[#0E0E0E] hover:bg-[#121212] border-b border-[#1A1A1A] px-5 py-4 sm:py-5 flex items-start justify-between gap-3 transition-colors group"
      data-testid={testId}
    >
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-[0.2em] mb-0.5">{label}</p>
        {badge && <p className="text-[10px] text-[#666] mb-1.5 italic">{badge}</p>}
        {preview ? (
          <p className="text-sm text-[#cccccc] leading-relaxed line-clamp-2">{preview}</p>
        ) : (
          <p className="text-sm text-[#555] italic">Tap to add</p>
        )}
      </div>
      <ChevronRight size={18} className="text-[#555] group-hover:text-[#D4AF37] flex-shrink-0 mt-0.5 transition-colors" />
    </button>
  );
}

function Drawer({ open, onClose, title, children, testId }) {
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in" onClick={onClose} data-testid={testId}>
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-lg bg-[#0E0E0E] border border-[#262626] sm:rounded-md rounded-t-md max-h-[90vh] flex flex-col animate-slide-up">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1A1A1A]">
          <p className="text-[11px] font-bold text-[#D4AF37] uppercase tracking-[0.25em]">{title}</p>
          <button onClick={onClose} className="text-[#888] hover:text-[#F5F5F5] transition-colors" data-testid={`${testId}-close`}>
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
      <style>{`
        @keyframes slide-up { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .animate-slide-up { animation: slide-up 200ms ease-out; }
      `}</style>
    </div>
  );
}

function EditSectionDrawer({ open, field, label, hint, initialValue, onClose, onSave, onAIImprove }) {
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [improving, setImproving] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null); // {text, response}

  useEffect(() => {
    if (open) {
      setDraft(initialValue || "");
      setAiSuggestion(null);
    }
  }, [open, initialValue]);

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(field, draft); onClose(); }
    catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const handleAI = async () => {
    setImproving(true);
    setAiSuggestion(null);
    try {
      const result = await onAIImprove(field, label, draft);
      if (result?.text) {
        setAiSuggestion(result);
      } else if (result?.message) {
        toast.message(result.message);
      } else {
        toast.message("No change suggested — your version may already be strong");
      }
    } catch { toast.error("AI improve failed"); }
    setImproving(false);
  };

  const acceptAI = () => {
    if (aiSuggestion?.text) {
      setDraft(aiSuggestion.text);
      setAiSuggestion(null);
      toast.success("AI version applied — tap Save to keep it");
    }
  };

  const rejectAI = () => setAiSuggestion(null);

  return (
    <Drawer open={open} onClose={onClose} title={label} testId={`drawer-${field}`}>
      <div className="p-5 space-y-4">
        {/* Helper hint */}
        {hint && (
          <p className="text-[11px] text-[#a89260] bg-[#181207] border border-[#D4AF37]/20 px-3 py-2 rounded-sm leading-relaxed">{hint}</p>
        )}
        <p className="text-[11px] text-[#666] leading-relaxed">
          Write or edit below. Or tap the AI button to get a suggestion you can accept or ignore.
        </p>

        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoFocus
          className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#D4AF37]/60 text-[#F5F5F5] text-sm leading-relaxed p-4 rounded-sm outline-none resize-y min-h-[140px]"
          placeholder={`Write your ${label.toLowerCase()}…`}
          data-testid={`drawer-${field}-textarea`}
        />

        <button
          onClick={handleAI}
          disabled={improving}
          className="w-full inline-flex items-center justify-center gap-2 bg-transparent border border-[#D4AF37]/40 text-[#D4AF37] font-semibold uppercase tracking-wider text-[11px] px-4 py-3 rounded-sm hover:bg-[#181207] transition-colors disabled:opacity-50"
          data-testid={`drawer-${field}-ai`}
        >
          {improving ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
          {improving ? "AI is thinking…" : aiSuggestion ? "Try another AI suggestion" : "Ask AI to improve this"}
        </button>

        {/* AI proposal card */}
        {aiSuggestion?.text && (
          <div className="border border-[#D4AF37]/40 bg-[#181207] rounded-sm overflow-hidden animate-slide-up" data-testid={`drawer-${field}-suggestion`}>
            <div className="px-4 py-2.5 bg-[#D4AF37]/10 border-b border-[#D4AF37]/30 flex items-center gap-2">
              <Sparkles size={12} className="text-[#D4AF37]" />
              <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-[0.2em]">AI suggestion</p>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-[#F5F5F5] leading-relaxed whitespace-pre-wrap">{aiSuggestion.text}</p>
              {aiSuggestion.response && aiSuggestion.response !== aiSuggestion.text && (
                <p className="text-[11px] text-[#a89260] italic">{aiSuggestion.response}</p>
              )}
              <div className="flex gap-2 pt-1">
                <button onClick={acceptAI} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#D4AF37] text-[#0A0A0A] font-bold uppercase tracking-wider text-[11px] px-3 py-2.5 rounded-sm hover:bg-[#E5C04A] transition-all" data-testid={`drawer-${field}-accept`}>
                  <Check size={12} /> Use this version
                </button>
                <button onClick={rejectAI} className="flex-1 inline-flex items-center justify-center gap-1.5 border border-[#262626] text-[#aaa] font-semibold uppercase tracking-wider text-[11px] px-3 py-2.5 rounded-sm hover:border-[#D4AF37]/40" data-testid={`drawer-${field}-reject`}>
                  <X size={12} /> Keep mine
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="border-t border-[#1A1A1A] p-4 bg-[#0A0A0A] flex gap-2 sticky bottom-0">
        <button onClick={onClose} className="flex-1 border border-[#262626] text-[#cfcfcf] uppercase tracking-wider text-[11px] px-4 py-3 rounded-full hover:border-[#D4AF37]/40 transition-colors">Cancel</button>
        <button onClick={handleSave} disabled={saving} className="flex-1 bg-[#D4AF37] text-[#0A0A0A] font-black uppercase tracking-wider text-[11px] px-4 py-3 rounded-full hover:bg-[#E5C04A] disabled:opacity-50 inline-flex items-center justify-center gap-1.5" data-testid={`drawer-${field}-save`}>
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
        </button>
      </div>
    </Drawer>
  );
}

function MediaDrawer({ open, onClose, profile, refreshProfile }) {
  const [video, setVideo] = useState("");
  const [video2, setVideo2] = useState("");
  const [contact, setContact] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && profile) {
      setVideo(profile.edited_fields?.highlight_video || profile.highlight_video || "");
      setVideo2(profile.edited_fields?.additional_videos?.[0] || "");
      setContact(profile.edited_fields?.contact_email || profile.onboarding_data?.contact?.contact_email || "");
    }
  }, [open, profile]);

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/media/upload?media_type=profile_image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshProfile();
      toast.success("Photo updated");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = { ...(profile.edited_fields || {}) };
      updates.highlight_video = video;
      updates.additional_videos = video2 ? [video2] : [];
      if (contact) updates.contact_email = contact;
      await api.put("/profile", { edited_fields: updates });
      if (contact) await api.post("/onboarding/save", { module: "contact", data: { contact_email: contact } });
      await refreshProfile();
      toast.success("Saved");
      onClose();
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const photoUrl = profile?.profile_image_path ? `${process.env.REACT_APP_BACKEND_URL}/api/media/${profile.profile_image_path}` : null;

  return (
    <Drawer open={open} onClose={onClose} title="Photos & Videos" testId="drawer-media">
      <div className="p-5 space-y-5">
        <div>
          <label className="text-[10px] font-bold text-[#666] uppercase tracking-[0.2em] flex items-center gap-1.5 mb-2.5">
            <Camera size={11} /> Profile photo
          </label>
          <div className="flex items-center gap-4">
            {photoUrl ? (
              <img src={photoUrl} alt="Profile" className="w-20 h-20 object-cover border border-[#262626] rounded-sm" data-testid="media-photo" />
            ) : (
              <div className="w-20 h-20 bg-[#0A0A0A] border border-[#262626] rounded-sm flex items-center justify-center">
                <Camera size={22} className="text-[#444]" />
              </div>
            )}
            <label className="flex-1">
              <span className="block bg-[#0A0A0A] border border-[#262626] text-[#cfcfcf] font-semibold uppercase tracking-wider text-[11px] px-4 py-3 rounded-sm hover:border-[#D4AF37]/40 transition-colors text-center cursor-pointer" data-testid="media-upload-btn">
                {uploading ? "Uploading…" : photoUrl ? "Change photo" : "Upload photo"}
              </span>
              <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); }} />
            </label>
          </div>
        </div>

        <div>
          <label className="text-[10px] font-bold text-[#666] uppercase tracking-[0.2em] flex items-center gap-1.5 mb-2">
            <Video size={11} /> Highlight video
          </label>
          <input type="url" value={video} onChange={(e) => setVideo(e.target.value)} placeholder="YouTube or Vimeo URL" className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#D4AF37]/60 text-[#F5F5F5] text-sm px-4 py-3 rounded-sm outline-none" data-testid="media-video-1" />
        </div>

        <div>
          <label className="text-[10px] font-bold text-[#666] uppercase tracking-[0.2em] flex items-center gap-1.5 mb-2">
            <Video size={11} /> Additional video <span className="text-[#444] normal-case">(optional)</span>
          </label>
          <input type="url" value={video2} onChange={(e) => setVideo2(e.target.value)} placeholder="Second video URL" className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#D4AF37]/60 text-[#F5F5F5] text-sm px-4 py-3 rounded-sm outline-none" data-testid="media-video-2" />
        </div>

        <div>
          <label className="text-[10px] font-bold text-[#666] uppercase tracking-[0.2em] flex items-center gap-1.5 mb-2">
            <MailIcon size={11} /> Scout contact email
          </label>
          <input type="email" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="The email scouts will use to reach you" className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#D4AF37]/60 text-[#F5F5F5] text-sm px-4 py-3 rounded-sm outline-none" data-testid="media-contact" />
        </div>
      </div>
      <div className="border-t border-[#1A1A1A] p-4 bg-[#0A0A0A] flex gap-2">
        <button onClick={onClose} className="flex-1 border border-[#262626] text-[#cfcfcf] uppercase tracking-wider text-[11px] px-4 py-3 rounded-full">Cancel</button>
        <button onClick={handleSave} disabled={saving} className="flex-1 bg-[#D4AF37] text-[#0A0A0A] font-black uppercase tracking-wider text-[11px] px-4 py-3 rounded-full hover:bg-[#E5C04A] disabled:opacity-50 inline-flex items-center justify-center gap-1.5" data-testid="media-save-btn">
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
        </button>
      </div>
    </Drawer>
  );
}

function AICoachButton({ profile, onApplyChange, fieldLabels }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const chatRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ from: "ai", text: "Hi — I'm your AI coach. Ask me to improve any section, or ask 'what's weak?' I'll suggest changes — you decide what to apply." }]);
    }
  }, [open, messages.length]);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setMessages((m) => [...m, { from: "user", text: userMsg }]);
    setInput(""); setSending(true);
    try {
      const { data } = await api.post("/ai/chat-edit", { message: userMsg });
      const reply = data.response || "Done.";
      // Build proposals object — only meaningful changes
      const proposals = {};
      if (data.changes && typeof data.changes === "object") {
        const currentEdited = profile?.edited_fields || {};
        const currentGen = profile?.generated_profile || {};
        const tact = currentGen.tactical_profile || {};
        for (const [k, v] of Object.entries(data.changes)) {
          if (!v || typeof v !== "string") continue;
          let currentVal = currentEdited[k] ?? currentGen[k] ?? "";
          if (k.startsWith("tactical_")) {
            currentVal = currentEdited[k] ?? tact[k.replace("tactical_", "")] ?? "";
          }
          if (v.trim() && v.trim() !== currentVal.trim()) proposals[k] = v;
        }
      }
      setMessages((m) => [...m, {
        from: "ai",
        text: reply,
        proposals: Object.keys(proposals).length > 0 ? proposals : null,
        applied: false,
      }]);
    } catch (e) {
      setMessages((m) => [...m, { from: "ai", text: e.response?.data?.detail || "Could not process that — try again." }]);
    }
    setSending(false);
  };

  const applyOne = async (msgIdx, field) => {
    const msg = messages[msgIdx];
    if (!msg?.proposals?.[field]) return;
    try {
      await onApplyChange({ [field]: msg.proposals[field] });
      setMessages((m) => m.map((x, i) => i === msgIdx ? { ...x, applied: { ...(x.applied || {}), [field]: true } } : x));
      toast.success(`${fieldLabels[field] || field} updated`);
    } catch { toast.error("Couldn't apply"); }
  };

  const applyAll = async (msgIdx) => {
    const msg = messages[msgIdx];
    if (!msg?.proposals) return;
    try {
      await onApplyChange(msg.proposals);
      const allApplied = Object.fromEntries(Object.keys(msg.proposals).map((k) => [k, true]));
      setMessages((m) => m.map((x, i) => i === msgIdx ? { ...x, applied: allApplied } : x));
      toast.success("All changes applied");
    } catch { toast.error("Couldn't apply"); }
  };

  const discardProposal = (msgIdx) => {
    setMessages((m) => m.map((x, i) => i === msgIdx ? { ...x, proposals: null } : x));
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0A] font-black uppercase tracking-wider text-xs px-4 py-3 rounded-full shadow-[0_10px_30px_-5px_rgba(212,175,55,0.45)] hover:bg-[#E5C04A] active:scale-[0.98] transition-all"
        data-testid="ai-coach-fab"
      >
        <Sparkles size={14} /> AI Coach
      </button>

      <Drawer open={open} onClose={() => setOpen(false)} title="AI Coach" testId="drawer-ai-coach">
        <div ref={chatRef} className="overflow-y-auto p-5 space-y-3 max-h-[60vh]">
          {messages.map((m, i) => (
            <div key={i}>
              <div className={`flex ${m.from === "ai" ? "justify-start" : "justify-end"}`}>
                <div className={`max-w-[88%] px-3.5 py-2.5 rounded-md text-sm leading-relaxed ${m.from === "ai" ? "bg-[#0A0A0A] border border-[#1A1A1A] text-[#dddddd]" : "bg-[#181207] border border-[#D4AF37]/30 text-[#E5E5E5]"}`}>
                  {m.text}
                </div>
              </div>
              {/* Proposal cards */}
              {m.proposals && Object.keys(m.proposals).map((field) => {
                const isApplied = m.applied && m.applied[field];
                return (
                  <div key={field} className={`mt-2 ml-2 border rounded-md overflow-hidden ${isApplied ? "border-green-500/30 bg-[#0a1a0a]/40" : "border-[#D4AF37]/40 bg-[#181207]"}`} data-testid={`coach-proposal-${field}`}>
                    <div className="px-3 py-2 flex items-center justify-between border-b border-[#D4AF37]/20">
                      <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-[0.18em]">
                        {isApplied ? "✓ APPLIED" : "PROPOSED CHANGE"} · {fieldLabels[field] || field}
                      </p>
                    </div>
                    <div className="p-3">
                      <p className="text-xs text-[#aaa] leading-relaxed whitespace-pre-wrap mb-2">{m.proposals[field]}</p>
                      {!isApplied && (
                        <div className="flex gap-1.5">
                          <button onClick={() => applyOne(i, field)} className="flex-1 bg-[#D4AF37] text-[#0A0A0A] font-bold uppercase tracking-wider text-[10px] py-2 rounded-sm hover:bg-[#E5C04A] inline-flex items-center justify-center gap-1" data-testid={`coach-apply-${field}`}>
                            <Check size={10} /> Apply
                          </button>
                          <button onClick={() => discardProposal(i)} className="flex-1 border border-[#262626] text-[#888] font-semibold uppercase tracking-wider text-[10px] py-2 rounded-sm hover:border-[#D4AF37]/40">
                            Discard
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {/* Apply all button if multiple */}
              {m.proposals && Object.keys(m.proposals).length > 1 && !Object.values(m.applied || {}).some(Boolean) && (
                <button onClick={() => applyAll(i)} className="mt-2 ml-2 text-[10px] text-[#D4AF37] font-bold uppercase tracking-wider hover:underline" data-testid={`coach-apply-all-${i}`}>
                  Apply all {Object.keys(m.proposals).length} changes
                </button>
              )}
            </div>
          ))}
          {sending && (
            <div className="flex justify-start"><div className="bg-[#0A0A0A] border border-[#1A1A1A] px-3.5 py-2.5 rounded-md text-sm text-[#666] inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Thinking…</div></div>
          )}
        </div>
        <div className="border-t border-[#1A1A1A] p-4 bg-[#0A0A0A] sticky bottom-0">
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              className="flex-1 bg-[#0E0E0E] border border-[#262626] focus:border-[#D4AF37]/60 text-[#F5F5F5] text-sm px-4 py-2.5 rounded-full outline-none"
              placeholder="Ask the AI…"
              data-testid="ai-coach-input"
            />
            <button onClick={send} disabled={sending || !input.trim()} className="bg-[#D4AF37] text-[#0A0A0A] px-4 rounded-full hover:bg-[#E5C04A] disabled:opacity-40" data-testid="ai-coach-send">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {["What's weak?", "Improve headline", "Strengthen overview", "Make it more specific"].map((q) => (
              <button key={q} onClick={() => setInput(q)} className="text-[10px] text-[#888] bg-[#0E0E0E] border border-[#1A1A1A] px-2.5 py-1 rounded-full hover:border-[#D4AF37]/30 hover:text-[#D4AF37] transition-colors">{q}</button>
            ))}
          </div>
        </div>
      </Drawer>
    </>
  );
}

function IdentityDrawer({ open, onClose, identity, onSave }) {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setData({
        full_name: identity?.full_name || "",
        date_of_birth: identity?.date_of_birth || "",
        nationality: identity?.nationality || "",
        second_nationality: identity?.second_nationality || "",
        height_cm: identity?.height_cm || "",
        weight_kg: identity?.weight_kg || "",
        primary_position: identity?.primary_position || "",
        secondary_position: identity?.secondary_position || "",
        preferred_foot: identity?.preferred_foot || "",
        current_club: identity?.current_club || "",
        agent_status: identity?.agent_status || "",
      });
    }
  }, [open, identity]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(data).forEach(([k, v]) => {
        if (v !== "" && v !== null) {
          if (k === "height_cm" || k === "weight_kg") payload[k] = parseFloat(v) || null;
          else payload[k] = v;
        }
      });
      await onSave(payload);
      onClose();
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  return (
    <Drawer open={open} onClose={onClose} title="Personal Details" testId="drawer-identity">
      <div className="p-5 space-y-4">
        <p className="text-[11px] text-[#666] leading-relaxed">These details appear at the top of your profile and help scouts find you in searches.</p>

        <FormField label="Full name">
          <input type="text" value={data.full_name || ""} onChange={(e) => set("full_name", e.target.value)} className="input-ed" data-testid="id-full-name" />
        </FormField>

        <FormField label="Date of birth" hint="YYYY-MM-DD format">
          <input type="date" value={data.date_of_birth || ""} onChange={(e) => set("date_of_birth", e.target.value)} className="input-ed" data-testid="id-dob" />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Nationality">
            <input type="text" value={data.nationality || ""} onChange={(e) => set("nationality", e.target.value)} className="input-ed" data-testid="id-nationality" placeholder="e.g. Nigeria" />
          </FormField>
          <FormField label="Second nationality" hint="Optional">
            <input type="text" value={data.second_nationality || ""} onChange={(e) => set("second_nationality", e.target.value)} className="input-ed" data-testid="id-second-nationality" />
          </FormField>
        </div>

        <FormField label="Primary position">
          <select value={data.primary_position || ""} onChange={(e) => set("primary_position", e.target.value)} className="input-ed" data-testid="id-primary-position">
            <option value="">Select position</option>
            {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </FormField>

        <FormField label="Secondary position" hint="Optional">
          <select value={data.secondary_position || ""} onChange={(e) => set("secondary_position", e.target.value)} className="input-ed" data-testid="id-secondary-position">
            <option value="">None</option>
            {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </FormField>

        <FormField label="Preferred foot">
          <div className="flex gap-2">
            {FEET.map((f) => (
              <button key={f} onClick={() => set("preferred_foot", f)} className={`flex-1 py-2.5 rounded-sm text-xs font-bold uppercase tracking-wider transition-colors ${data.preferred_foot === f ? "bg-[#D4AF37] text-[#0A0A0A]" : "bg-[#0A0A0A] border border-[#262626] text-[#888] hover:border-[#D4AF37]/40"}`} data-testid={`id-foot-${f.toLowerCase()}`}>
                {f}
              </button>
            ))}
          </div>
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Height (cm)">
            <input type="number" min="120" max="230" value={data.height_cm || ""} onChange={(e) => set("height_cm", e.target.value)} className="input-ed" data-testid="id-height" />
          </FormField>
          <FormField label="Weight (kg)">
            <input type="number" min="35" max="150" value={data.weight_kg || ""} onChange={(e) => set("weight_kg", e.target.value)} className="input-ed" data-testid="id-weight" />
          </FormField>
        </div>

        <FormField label="Current club">
          <input type="text" value={data.current_club || ""} onChange={(e) => set("current_club", e.target.value)} className="input-ed" data-testid="id-club" placeholder="e.g. Lagos FC Academy" />
        </FormField>

        <FormField label="Agent status">
          <select value={data.agent_status || ""} onChange={(e) => set("agent_status", e.target.value)} className="input-ed" data-testid="id-agent">
            <option value="">Not specified</option>
            {AGENT_STATUSES.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </FormField>
      </div>
      <div className="border-t border-[#1A1A1A] p-4 bg-[#0A0A0A] flex gap-2 sticky bottom-0">
        <button onClick={onClose} className="flex-1 border border-[#262626] text-[#cfcfcf] uppercase tracking-wider text-[11px] px-4 py-3 rounded-full">Cancel</button>
        <button onClick={handleSave} disabled={saving} className="flex-1 bg-[#D4AF37] text-[#0A0A0A] font-black uppercase tracking-wider text-[11px] px-4 py-3 rounded-full hover:bg-[#E5C04A] disabled:opacity-50 inline-flex items-center justify-center gap-1.5" data-testid="id-save-btn">
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
        </button>
      </div>
      <style>{`
        .input-ed { width:100%; background:#0A0A0A; border:1px solid #262626; color:#F5F5F5; font-size:14px; padding:11px 13px; border-radius:2px; outline:none; transition:border-color 150ms ease; }
        .input-ed:focus { border-color:rgba(212,175,55,0.5); }
      `}</style>
    </Drawer>
  );
}

function FormField({ label, hint, children }) {
  return (
    <div>
      <label className="text-[10px] font-bold text-[#666] uppercase tracking-[0.2em] block mb-1.5">{label}{hint && <span className="text-[#444] normal-case ml-1">· {hint}</span>}</label>
      {children}
    </div>
  );
}

function StrengthsDrawer({ open, onClose, attrs, onSave }) {
  const [groups, setGroups] = useState({ technical: [], tactical: [], physical: [], mental: [] });
  const [input, setInput] = useState({ technical: "", tactical: "", physical: "", mental: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setGroups({
        technical: Array.isArray(attrs?.technical) ? [...attrs.technical] : [],
        tactical: Array.isArray(attrs?.tactical) ? [...attrs.tactical] : [],
        physical: Array.isArray(attrs?.physical) ? [...attrs.physical] : [],
        mental: Array.isArray(attrs?.mental) ? [...attrs.mental] : [],
      });
      setInput({ technical: "", tactical: "", physical: "", mental: "" });
    }
  }, [open, attrs]);

  const addItem = (cat) => {
    const val = (input[cat] || "").trim();
    if (!val) return;
    if (groups[cat].some((i) => i.toLowerCase() === val.toLowerCase())) {
      toast.message("Already added");
      return;
    }
    setGroups((g) => ({ ...g, [cat]: [...g[cat], val] }));
    setInput((i) => ({ ...i, [cat]: "" }));
  };

  const removeItem = (cat, idx) => {
    setGroups((g) => ({ ...g, [cat]: g[cat].filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(groups);
      onClose();
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const CAT_HINT = {
    technical: "Ball skills — first touch, passing, finishing, dribbling",
    tactical: "Game intelligence — positioning, decision-making, reading the game",
    physical: "Body — pace, strength, stamina, jumping",
    mental: "Mindset — composure, leadership, work-rate, resilience",
  };

  return (
    <Drawer open={open} onClose={onClose} title="Your Strengths" testId="drawer-strengths">
      <div className="p-5 space-y-5">
        <p className="text-[11px] text-[#666] leading-relaxed">
          Add what you're genuinely good at, in each category. Be honest — overstated strengths hurt your credibility with scouts.
        </p>

        {["technical", "tactical", "physical", "mental"].map((cat) => (
          <div key={cat} data-testid={`strengths-${cat}`}>
            <label className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-[0.2em] block mb-1">{cat}</label>
            <p className="text-[10px] text-[#666] italic mb-2">{CAT_HINT[cat]}</p>

            {/* Chips */}
            {groups[cat].length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2.5">
                {groups[cat].map((item, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 bg-[#181207] border border-[#D4AF37]/30 text-[#E5E5E5] text-xs px-2.5 py-1.5 rounded-full" data-testid={`chip-${cat}-${i}`}>
                    {item}
                    <button onClick={() => removeItem(cat, i)} className="text-[#888] hover:text-red-400" aria-label="Remove" data-testid={`chip-remove-${cat}-${i}`}>
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Add input */}
            <div className="flex gap-1.5">
              <input
                type="text"
                value={input[cat]}
                onChange={(e) => setInput((s) => ({ ...s, [cat]: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(cat); } if (e.key === ",") { e.preventDefault(); addItem(cat); } }}
                placeholder={`Add a ${cat} strength…`}
                className="flex-1 bg-[#0A0A0A] border border-[#262626] focus:border-[#D4AF37]/60 text-[#F5F5F5] text-xs px-3 py-2 rounded-sm outline-none transition-colors"
                data-testid={`add-input-${cat}`}
              />
              <button onClick={() => addItem(cat)} className="bg-transparent border border-[#D4AF37]/40 text-[#D4AF37] uppercase tracking-wider text-[10px] font-bold px-3 rounded-sm hover:bg-[#181207]" data-testid={`add-btn-${cat}`}>
                Add
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="border-t border-[#1A1A1A] p-4 bg-[#0A0A0A] flex gap-2 sticky bottom-0">
        <button onClick={onClose} className="flex-1 border border-[#262626] text-[#cfcfcf] uppercase tracking-wider text-[11px] px-4 py-3 rounded-full">Cancel</button>
        <button onClick={handleSave} disabled={saving} className="flex-1 bg-[#D4AF37] text-[#0A0A0A] font-black uppercase tracking-wider text-[11px] px-4 py-3 rounded-full hover:bg-[#E5C04A] disabled:opacity-50 inline-flex items-center justify-center gap-1.5" data-testid="strengths-save-btn">
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
        </button>
      </div>
    </Drawer>
  );
}

function StrengthSummary({ strength }) {
  const [expanded, setExpanded] = useState(false);
  if (!strength || strength.score === undefined) return null;
  const { score, actions = [], publish_ready } = strength;
  return (
    <div className="bg-[#0E0E0E] border-b border-[#1A1A1A]">
      <button onClick={() => setExpanded((e) => !e)} className="w-full px-5 py-3.5 flex items-center justify-between gap-3" data-testid="strength-toggle">
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative w-9 h-9 flex-shrink-0">
            <svg viewBox="0 0 36 36" className="w-9 h-9 -rotate-90">
              <circle cx="18" cy="18" r="14" fill="none" stroke="#262626" strokeWidth="3" />
              <circle cx="18" cy="18" r="14" fill="none" stroke={scoreColor(score)} strokeWidth="3" strokeDasharray={`${(score / 100) * 88} 88`} strokeLinecap="round" />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold tabular-nums text-[#F5F5F5]">{score}</span>
          </div>
          <div className="text-left min-w-0">
            <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-[0.2em]">Profile strength</p>
            <p className="text-xs text-[#cfcfcf]">{scoreLabel(score)} {publish_ready ? "· Publish-ready" : `· ${actions[0]?.text || "Keep improving"}`}</p>
          </div>
        </div>
        {expanded ? <ChevronUp size={16} className="text-[#666]" /> : <ChevronDown size={16} className="text-[#666]" />}
      </button>
      {expanded && actions.length > 0 && (
        <div className="px-5 pb-4 space-y-1.5" data-testid="strength-actions">
          {actions.slice(0, 5).map((a, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-[#888]">
              <span className="w-1 h-1 rounded-full bg-[#D4AF37]" />
              <span>{a.text}</span>
              {a.points && <span className="text-[#555]">+{a.points}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────── PAGE ──────────────────────────── */

export default function ProfileEditor() {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [strength, setStrength] = useState(null);
  const [loading, setLoading] = useState(true);
  const [improving, setImproving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [editingField, setEditingField] = useState(null); // {field, label, value}
  const [mediaOpen, setMediaOpen] = useState(false);
  const [identityOpen, setIdentityOpen] = useState(false);
  const [strengthsOpen, setStrengthsOpen] = useState(false);
  const navigate = useNavigate();

  const refreshProfile = useCallback(async () => {
    const { data } = await api.get("/profile");
    setProfile(data.profile);
    setStrength(data.strength || null);
  }, []);

  useEffect(() => {
    (async () => {
      if (user === false) { navigate("/login"); return; }
      if (!user) return;
      try {
        await refreshProfile();
      } catch (e) {
        if (e.response?.status === 404) { navigate("/onboarding"); return; }
      }
      setLoading(false);
    })();
  }, [user, navigate, refreshProfile]);

  // Stripe return — auto-publish after payment
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    (async () => {
      let attempts = 0;
      while (attempts < 10) {
        try {
          const { data } = await api.get(`/payments/status/${sessionId}`);
          if (data.payment_status === "paid") {
            toast.success("Payment confirmed — your profile is live!");
            if (refreshUser) await refreshUser();
            await refreshProfile();
            break;
          }
        } catch {}
        await new Promise((r) => setTimeout(r, 2000));
        attempts++;
      }
    })();
  }, [searchParams, refreshUser, refreshProfile]);

  const handleSectionSave = async (field, value) => {
    const updates = { ...(profile.edited_fields || {}), [field]: value };
    await api.put("/profile", { edited_fields: updates });
    await refreshProfile();
    toast.success("Saved");
  };

  const handleAIImprove = async (field, label, currentValue) => {
    try {
      const { data } = await api.post("/ai/chat-edit", {
        message: `Improve my ${label.toLowerCase()}. Current: "${currentValue || "(empty)"}". Make it sharper, scout-ready, and authentic. Return your improved version in the changes field for "${field}".`,
      });
      const newVal = data.changes?.[field];
      if (newVal && newVal.trim() && newVal !== currentValue) {
        return { text: newVal, response: data.response };
      }
      return { message: data.response || "No change suggested — your version may already be strong." };
    } catch (e) {
      throw e;
    }
  };

  const handleAICoachChange = async (changes) => {
    const updates = { ...(profile.edited_fields || {}), ...changes };
    await api.put("/profile", { edited_fields: updates });
    await refreshProfile();
    toast.success("AI applied changes");
  };

  const handlePublish = async () => {
    if (!user?.plan) { navigate("/paywall"); return; }
    setPublishing(true);
    try {
      await api.post("/profile/publish");
      toast.success("Profile published!");
      await refreshProfile();
    } catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
    setPublishing(false);
  };

  const handleIdentitySave = async (payload) => {
    await api.put("/profile/identity", payload);
    await refreshProfile();
    toast.success("Details saved");
  };

  const handleStrengthsSave = async (groups) => {
    const updates = { ...(profile.edited_fields || {}), key_attributes: groups };
    await api.put("/profile", { edited_fields: updates });
    await refreshProfile();
    toast.success("Strengths saved");
  };

  if (loading || !profile) {
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="animate-spin text-[#D4AF37]" size={24} /></div>;
  }

  const gen = profile.generated_profile || {};
  const edited = profile.edited_fields || {};
  const get = (key) => edited[key] ?? gen[key] ?? "";
  const tact = gen.tactical_profile || {};
  const identity = profile.onboarding_data?.identity || {};

  const photoUrl = profile.profile_image_path ? `${process.env.REACT_APP_BACKEND_URL}/api/media/${profile.profile_image_path}` : null;
  const isPublished = profile.status === "published";
  const isPaid = Boolean(user?.plan);
  const publicUrl = isPublished && profile.slug ? `${window.location.origin}/player/${profile.slug}` : null;

  const sections = [
    { field: "headline", label: "Headline", value: get("headline") },
    { field: "overview", label: "Overview", value: get("overview") },
    { field: "playing_style", label: "Playing Style", value: get("playing_style") },
  ];

  return (
    <div className="bg-[#0A0A0A] min-h-screen" data-testid="profile-editor">
      <UnlockBar />

      <div className="max-w-2xl mx-auto pb-24">
        {/* Header bar */}
        <div className="px-5 py-3 border-b border-[#1A1A1A] flex items-center justify-between">
          <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-[#888] hover:text-[#D4AF37] text-xs uppercase tracking-wider transition-colors" data-testid="editor-back">
            <ArrowLeft size={14} /> Back
          </Link>
          <p className="text-[10px] text-[#888] tracking-[0.25em] uppercase">Profile</p>
          {publicUrl ? (
            <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#D4AF37] hover:text-[#E5C04A] text-xs uppercase tracking-wider transition-colors" data-testid="editor-view-public">
              <ExternalLink size={12} /> View
            </a>
          ) : (
            <Link to="/preview" className="inline-flex items-center gap-1.5 text-[#D4AF37] hover:text-[#E5C04A] text-xs uppercase tracking-wider transition-colors" data-testid="editor-preview">
              <Eye size={12} /> Preview
            </Link>
          )}
        </div>

        {/* Hero card */}
        <div className="px-5 py-6 bg-[#0E0E0E] border-b border-[#1A1A1A]" data-testid="editor-hero">
          <div className="flex items-center gap-4 mb-4">
            <button onClick={() => setMediaOpen(true)} className="relative w-20 h-20 rounded-md overflow-hidden border border-[#262626] hover:border-[#D4AF37]/40 transition-colors flex-shrink-0 bg-[#0A0A0A]" data-testid="editor-photo-btn">
              {photoUrl ? (
                <img src={photoUrl} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center"><Camera size={20} className="text-[#444]" /></div>
              )}
              <span className="absolute inset-x-0 bottom-0 text-center text-[9px] py-1 bg-black/60 text-[#D4AF37] font-bold uppercase tracking-wider">{photoUrl ? "Change" : "Add"}</span>
            </button>
            <button onClick={() => setIdentityOpen(true)} className="min-w-0 flex-1 text-left group" data-testid="editor-identity-tap">
              <h1 className="text-xl sm:text-2xl font-black tracking-tight text-[#F5F5F5] truncate group-hover:text-[#D4AF37] transition-colors flex items-center gap-1.5">
                {identity.full_name || user.name || "Your profile"}
                <ChevronRight size={16} className="text-[#555] group-hover:text-[#D4AF37] transition-colors" />
              </h1>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {identity.primary_position && <span className="text-[10px] text-[#D4AF37] border border-[#262626] px-2 py-0.5 uppercase tracking-wider rounded-sm">{identity.primary_position}</span>}
                {identity.preferred_foot && <span className="text-[10px] text-[#888] border border-[#262626] px-2 py-0.5 uppercase tracking-wider rounded-sm">{identity.preferred_foot}-footed</span>}
                {identity.nationality && <span className="text-[10px] text-[#888] border border-[#262626] px-2 py-0.5 uppercase tracking-wider rounded-sm">{identity.nationality}</span>}
                <span className={`text-[10px] px-2 py-0.5 uppercase tracking-wider rounded-sm border ${isPublished ? "border-green-500/40 text-green-400" : "border-[#444] text-[#888]"}`}>{isPublished ? "Live" : "Draft"}</span>
              </div>
              <p className="text-[10px] text-[#666] mt-1.5 group-hover:text-[#D4AF37]/70 transition-colors uppercase tracking-wider">Tap to edit name, age, position, club…</p>
            </button>
          </div>

          {/* Primary action */}
          {!isPublished ? (
            <button onClick={handlePublish} disabled={publishing} className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A0A0A] font-black tracking-widest uppercase text-xs px-6 py-3.5 rounded-full hover:bg-[#E5C04A] active:scale-[0.98] transition-all disabled:opacity-60" data-testid="editor-publish-btn">
              {isPaid ? <Globe size={13} /> : <Lock size={13} />}
              {publishing ? "PUBLISHING..." : isPaid ? "PUBLISH TO DIRECTORY" : "PUBLISH — $19"}
            </button>
          ) : (
            <Link to={publicUrl || "#"} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 border border-green-500/30 bg-[#0a1a0a] text-green-400 font-bold uppercase tracking-wider text-xs px-6 py-3.5 rounded-full hover:bg-[#0e2010] transition-colors" data-testid="editor-published-btn">
              <Check size={13} /> Live in directory · View
            </Link>
          )}
        </div>

        {/* Strength meter */}
        <StrengthSummary strength={strength} />

        {/* Intro / how-to */}
        <div className="px-5 py-4 bg-[#0A0A0A] border-b border-[#1A1A1A]" data-testid="editor-intro">
          <div className="flex items-start gap-2.5">
            <div className="w-7 h-7 rounded-full bg-[#181207] border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
              <Sparkles size={11} className="text-[#D4AF37]" />
            </div>
            <div className="text-xs text-[#9a9a9a] leading-relaxed">
              Tap any section to edit it. Inside, you can <span className="text-[#D4AF37] font-semibold">Ask AI to improve</span> — it suggests a new version, you choose whether to keep it. AI never changes anything without your approval.
            </div>
          </div>
        </div>

        {/* Section list */}
        <div data-testid="editor-section-list">
          {sections.map((s) => (
            <SectionRow
              key={s.field}
              label={s.label}
              value={s.value}
              onTap={() => setEditingField({ ...s })}
              testId={`row-${s.field}`}
            />
          ))}

          {/* Tactical (3 sub-sections grouped) */}
          <TacticalGroup
            tact={tact}
            edited={edited}
            onTap={(field, label, hint) => setEditingField({ field, label, hint, value: edited[field] ?? tact[field.replace("tactical_", "")] ?? "" })}
          />

          {/* Strengths — editable */}
          <StrengthsRow attrs={(edited.key_attributes ?? gen.key_attributes) || {}} onTap={() => setStrengthsOpen(true)} />

          {/* Continue editables */}
          <SectionRow label="Development Focus" value={get("development_focus")} onTap={() => setEditingField({ field: "development_focus", label: "Development Focus", value: get("development_focus") })} testId="row-development_focus" />
          <SectionRow label="Experience" value={get("experience_summary")} onTap={() => setEditingField({ field: "experience_summary", label: "Experience", value: get("experience_summary") })} testId="row-experience_summary" />

          {/* Media row */}
          <button onClick={() => setMediaOpen(true)} className="w-full text-left bg-[#0E0E0E] hover:bg-[#121212] border-b border-[#1A1A1A] px-5 py-4 sm:py-5 flex items-center justify-between gap-3 transition-colors group" data-testid="row-media">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-[0.2em] mb-1">Photo, video & contact</p>
              <p className="text-sm text-[#cccccc]">
                {photoUrl ? "Photo added" : "Add photo"} · {profile.edited_fields?.highlight_video || profile.highlight_video ? "Video added" : "No video"} · {profile.edited_fields?.contact_email ? "Contact set" : "No contact"}
              </p>
            </div>
            <ChevronRight size={18} className="text-[#555] group-hover:text-[#D4AF37] flex-shrink-0 transition-colors" />
          </button>
        </div>

        {/* Help footer */}
        <p className="text-[11px] text-[#555] text-center px-5 mt-6 leading-relaxed">
          Tap any section to edit. AI Coach can rewrite any part of your profile — find it in the gold button below.
        </p>
      </div>

      {/* Edit drawer */}
      <EditSectionDrawer
        open={Boolean(editingField)}
        field={editingField?.field}
        label={editingField?.label || ""}
        hint={editingField?.hint}
        initialValue={editingField?.value}
        onClose={() => setEditingField(null)}
        onSave={handleSectionSave}
        onAIImprove={handleAIImprove}
      />

      {/* Media drawer */}
      <MediaDrawer open={mediaOpen} onClose={() => setMediaOpen(false)} profile={profile} refreshProfile={refreshProfile} />

      {/* Identity drawer */}
      <IdentityDrawer open={identityOpen} onClose={() => setIdentityOpen(false)} identity={identity} onSave={handleIdentitySave} />

      {/* Strengths drawer */}
      <StrengthsDrawer open={strengthsOpen} onClose={() => setStrengthsOpen(false)} attrs={(edited.key_attributes ?? gen.key_attributes) || {}} onSave={handleStrengthsSave} />

      {/* Floating AI Coach */}
      <AICoachButton profile={profile} onApplyChange={handleAICoachChange} fieldLabels={FIELD_LABELS} />

      {/* Smart publish nudge — only triggers for unpaid users with a generated profile */}
      <PublishNudge />
    </div>
  );
}

/* ──────────────────────────── SUB-COMPONENTS ──────────────────────────── */

function TacticalGroup({ tact, edited, onTap }) {
  const items = [
    { field: "tactical_in_possession", label: "Tactical · In Possession", source: tact.in_possession, hint: "How you play when YOUR team has the ball" },
    { field: "tactical_out_of_possession", label: "Tactical · Out of Possession", source: tact.out_of_possession, hint: "How you defend when the OTHER team has the ball" },
    { field: "tactical_transitions", label: "Tactical · Transitions", source: tact.transitions, hint: "What you do in the moments after gaining or losing the ball" },
  ].filter((i) => i.source !== undefined);
  if (items.length === 0) return null;
  return (
    <>
      {items.map((i) => (
        <SectionRow
          key={i.field}
          label={i.label}
          value={edited[i.field] ?? i.source}
          onTap={() => onTap(i.field, i.label, i.hint)}
          testId={`row-${i.field}`}
          badge={i.hint}
        />
      ))}
    </>
  );
}

function AttributesRow({ attrs }) {
  const [expanded, setExpanded] = useState(false);
  const categories = ["technical", "tactical", "physical", "mental"].filter((c) => attrs[c]?.length);
  if (categories.length === 0) return null;
  return (
    <div className="bg-[#0E0E0E] border-b border-[#1A1A1A]">
      <button onClick={() => setExpanded((e) => !e)} className="w-full px-5 py-4 sm:py-5 flex items-start justify-between gap-3" data-testid="row-attributes">
        <div className="min-w-0 flex-1 text-left">
          <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-[0.2em] mb-1">Strengths <span className="text-[#666] normal-case">· read-only</span></p>
          <p className="text-sm text-[#cccccc]">
            {categories.map((c) => `${attrs[c].length} ${c}`).join(" · ")}
          </p>
        </div>
        {expanded ? <ChevronUp size={18} className="text-[#555] mt-1" /> : <ChevronDown size={18} className="text-[#555] mt-1" />}
      </button>
      {expanded && (
        <div className="px-5 pb-5 grid grid-cols-2 gap-3" data-testid="attributes-expanded">
          {categories.map((c) => (
            <div key={c} className="bg-[#0A0A0A] border border-[#1A1A1A] p-3 rounded-sm">
              <p className="text-[9px] font-bold text-[#666] uppercase tracking-wider mb-1.5">{c}</p>
              <ul className="space-y-1">
                {attrs[c].map((a, j) => (
                  <li key={j} className="text-[11px] text-[#aaa] flex items-start gap-1.5"><span className="text-[#D4AF37]/60 mt-0.5">•</span>{a}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StrengthsRow({ attrs, onTap }) {
  const categories = ["technical", "tactical", "physical", "mental"];
  const counts = categories.map((c) => (attrs[c]?.length || 0));
  const total = counts.reduce((a, b) => a + b, 0);
  const preview = total > 0 ? categories.filter((c) => attrs[c]?.length).map((c) => `${attrs[c].length} ${c}`).join(" · ") : "Tap to add what you're good at";
  return (
    <button
      onClick={onTap}
      className="w-full text-left bg-[#0E0E0E] hover:bg-[#121212] border-b border-[#1A1A1A] px-5 py-4 sm:py-5 flex items-start justify-between gap-3 transition-colors group"
      data-testid="row-strengths"
    >
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-[0.2em] mb-0.5">Strengths</p>
        <p className="text-[10px] text-[#666] mb-1.5 italic">What you're genuinely good at — 4 categories</p>
        <p className={`text-sm leading-relaxed ${total > 0 ? "text-[#cccccc]" : "text-[#555] italic"}`}>{preview}</p>
      </div>
      <ChevronRight size={18} className="text-[#555] group-hover:text-[#D4AF37] flex-shrink-0 mt-0.5 transition-colors" />
    </button>
  );
}
